import { Controller } from '@hotwired/stimulus';

/**
 * Master/detail picker for the « Appliquer un template » modal.
 *
 * Architecture:
 *   * Left column: search input + scrollable list of templates (one button per
 *     row, marked as `listItem` target).
 *   * Right column: ALL preview panels rendered server-side at modal mount
 *     time, all but the active one carrying the `hidden` attribute. Selection
 *     toggles visibility — no Turbo Frame round-trip per click. The brief's
 *     intent ("le serveur retourne le détail rendu, pas de logique de rendu
 *     JS côté client") is preserved: the controller never builds HTML, it
 *     only flips visibility on pre-rendered server output.
 *   * Hidden `template_id` input inside the form picks up the selected id
 *     before submit. Edit / delete links + the importedCount label are also
 *     rewritten on selection.
 *
 * Markup contract: see `apply_template_modal_component.html.erb`.
 */
export default class extends Controller {
  static targets = [
    'searchInput',
    'listItem',
    'previewPanel',
    'templateIdInput',
    'importedCount',
    'editLink',
    'deleteLink',
    'noResults',
    'nameDisplay',
  ];

  static values = {
    editTemplate: String,
    deleteTemplate: String,
    confirmTemplate: String,
    importedCountTemplate: String,
  };

  connect() {
    const initial =
      this.listItemTargets.find((el) => el.dataset.selected === 'true') || this.listItemTargets[0];
    if (initial) this.#activate(initial);
  }

  // ── Selection ───────────────────────────────────────────────────────────

  select(event) {
    const item = event.currentTarget;
    this.#activate(item);
  }

  // ── Search filter (clientside) ──────────────────────────────────────────

  filter(event) {
    const query = event.target.value.trim().toLowerCase();
    let visible = 0;

    this.listItemTargets.forEach((item) => {
      const haystack = (item.dataset.searchHaystack || '').toLowerCase();
      const match = !query || haystack.includes(query);
      item.hidden = !match;
      if (match) visible += 1;
    });

    if (this.hasNoResultsTarget) {
      this.noResultsTarget.hidden = visible !== 0;
    }

    // If the currently-selected item got filtered out, surface the first
    // visible item so the right panel never shows a hidden template.
    const activeStillVisible = this.listItemTargets.some(
      (el) => el.dataset.selected === 'true' && !el.hidden
    );
    if (!activeStillVisible) {
      const firstVisible = this.listItemTargets.find((el) => !el.hidden);
      if (firstVisible) this.#activate(firstVisible);
    }
  }

  // ── Keyboard navigation (arrow up/down inside the list) ─────────────────

  keyboard(event) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();

    const visibleItems = this.listItemTargets.filter((el) => !el.hidden);
    if (visibleItems.length === 0) return;

    const currentIdx = visibleItems.findIndex((el) => el.dataset.selected === 'true');
    const nextIdx =
      event.key === 'ArrowDown'
        ? Math.min(currentIdx + 1, visibleItems.length - 1)
        : Math.max(currentIdx - 1, 0);

    const next = visibleItems[nextIdx];
    if (next) {
      this.#activate(next);
      next.focus();
    }
  }

  // ── Private ─────────────────────────────────────────────────────────────

  #activate(item) {
    const id = item.dataset.templateId;
    const name = item.dataset.templateName || '';
    const lineCount = parseInt(item.dataset.lineCount || '0', 10);

    // Update list selection state.
    this.listItemTargets.forEach((el) => {
      const selected = el === item;
      el.dataset.selected = selected ? 'true' : 'false';
      el.setAttribute('aria-selected', selected ? 'true' : 'false');
      el.tabIndex = selected ? 0 : -1;
    });

    // Show the matching preview panel; hide the others.
    this.previewPanelTargets.forEach((panel) => {
      panel.hidden = panel.dataset.templateId !== id;
    });

    // Submit value.
    if (this.hasTemplateIdInputTarget) {
      this.templateIdInputTarget.value = id;
    }

    // Right-column header — name display.
    if (this.hasNameDisplayTarget) {
      this.nameDisplayTarget.textContent = name;
    }

    // Edit / delete links — rewrite href + confirm message.
    if (this.hasEditLinkTarget && this.hasEditTemplateValue) {
      this.editLinkTarget.href = this.editTemplateValue.replace('__ID__', id);
    }
    if (this.hasDeleteLinkTarget && this.hasDeleteTemplateValue) {
      this.deleteLinkTarget.href = this.deleteTemplateValue.replace('__ID__', id);
      // The styled `Ui::ConfirmationModalComponent` flow reads the message
      // from the trigger's value attribute (Stimulus values are reactive,
      // so the change propagates automatically when the trigger fires).
      // We update the value attr — NOT `dataset.turboConfirm`, which only
      // applies to the native `confirm()` flow we just retired.
      //
      // The name is substituted RAW: the modal renders its message as TEXT
      // NODES (XSS QA #556), so it needs no escaping — and escaping it here
      // would surface the entities to the user (« Mission d'architecte » →
      // « Mission d&#39;architecte »).
      if (this.hasConfirmTemplateValue) {
        this.deleteLinkTarget.setAttribute(
          'data-confirmation-modal-trigger-message-value',
          this.confirmTemplateValue.replace('__NAME__', name)
        );
      }
    }

    // Footer counter (« X lignes seront importées »).
    if (this.hasImportedCountTarget && this.hasImportedCountTemplateValue) {
      this.importedCountTarget.textContent = this.importedCountTemplateValue.replace(
        '__COUNT__',
        lineCount.toString()
      );
    }
  }
}
